/* ↗ .cursor/references/code-notes/r-img.js.md#§1 */

const ARMED_ATTR = 'data-r-img-armed';
const LOADED_ATTR = 'data-r-loaded';
const SELECTOR = 'img.r-img';

/* ↗ .cursor/references/code-notes/r-img.js.md#§2 */
const STALL_MS = 2500;
const MAX_RETRIES = 2;
/* ↗ .cursor/references/code-notes/r-img.js.md#§3 */
const STALL_ROOT_MARGIN = '300px 0px';

/** @type {WeakMap<Element, { tries: number, timer: ReturnType<typeof setTimeout> | undefined }>} */
const watched = new WeakMap();

/** @type {IntersectionObserver | undefined} */
let stallObserver;

/** @param {Element} img */
function unwatch(img) {
  const state = watched.get(img);
  if (state?.timer) clearTimeout(state.timer);
  watched.delete(img);
  stallObserver?.unobserve(img);
}

/** @param {Element} el */
function markLoaded(el) {
  if (el instanceof HTMLImageElement && !el.hasAttribute(LOADED_ATTR)) {
    el.setAttribute(LOADED_ATTR, '');
  }
}

/**
 * Re-issues a hung request. Nothing is cleared first: blanking src/srcset makes
 * the element fire `error`, and the error listener would mark it loaded — the
 * opposite of the intent. Dropping the lazy deferral takes it out of the queue;
 * re-assigning `srcset` (or `src`) re-runs source selection, which is what
 * actually aborts the hung fetch and starts a new one.
 *
 * fetchpriority stays untouched until the LAST attempt. A network blip can
 * stall a whole screenful at once, and a herd of high-priority retries would
 * compete with whatever the page legitimately wants next; one final
 * escalation per image is enough.
 * @param {HTMLImageElement} img
 * @param {number} attempt
 */
function restart(img, attempt) {
  img.loading = 'eager';
  if (attempt >= MAX_RETRIES) img.fetchPriority = 'high';
  if (img.srcset) img.srcset = img.srcset;
  else if (img.src) img.src = img.src;
}

/** @param {HTMLImageElement} img */
function checkStalled(img) {
  if (!img.isConnected || img.complete) {
    unwatch(img);
    return;
  }

  const state = watched.get(img);
  if (!state) return;

  if (state.tries >= MAX_RETRIES) {
    unwatch(img);
    return;
  }

  state.tries += 1;
  restart(img, state.tries);
  state.timer = setTimeout(() => checkStalled(img), STALL_MS * (state.tries + 1));
}

if ('IntersectionObserver' in window) {
  stallObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const img = /** @type {HTMLImageElement} */ (entry.target);

        if (img.complete) {
          unwatch(img);
          continue;
        }

        if (!entry.isIntersecting) {
          // Out of reach again: stop the clock rather than retrying something
          // nobody is looking at. Tries are kept; the clock re-arms on re-entry.
          const state = watched.get(img);
          if (state?.timer) clearTimeout(state.timer);
          if (state) state.timer = undefined;
          continue;
        }

        const state = watched.get(img) ?? { tries: 0, timer: undefined };
        if (state.timer) continue;
        state.timer = setTimeout(() => checkStalled(img), STALL_MS);
        watched.set(img, state);
      }
    },
    { rootMargin: STALL_ROOT_MARGIN }
  );
}

/**
 * One pass over a subtree: already-complete images reveal (cached images fire no
 * load event), incomplete ones go under the watchdog. The single entry point for
 * "this DOM may contain images the listeners haven't met" — startup, section
 * renders, and MutationObserver insertions all funnel through here.
 * @param {ParentNode} [root]
 */
function absorb(root = document) {
  for (const img of root.querySelectorAll(SELECTOR)) {
    if (/** @type {HTMLImageElement} */ (img).complete) markLoaded(img);
    else stallObserver?.observe(img);
  }
}

// Capture phase: 'load' does not bubble from <img>, so a listener on document
// only sees it during capture. One listener covers every image, present and future.
document.addEventListener(
  'load',
  (event) => {
    const target = event.target;
    if (target instanceof HTMLImageElement && target.matches(SELECTOR)) {
      markLoaded(target);
      unwatch(target);
    }
  },
  true
);

// Same treatment for errors — a 404 image must reveal rather than hang at opacity:0.
document.addEventListener(
  'error',
  (event) => {
    const target = event.target;
    if (target instanceof HTMLImageElement && target.matches(SELECTOR)) {
      markLoaded(target);
      unwatch(target);
    }
  },
  true
);

absorb();
document.documentElement.setAttribute(ARMED_ATTR, '');

// Re-absorb after section renders: newly inserted images that were already in
// cache are `complete` on insertion and fire no load event.
for (const eventName of ['DOMContentLoaded', 'shopify:section:load']) {
  document.addEventListener(eventName, () => absorb());
}

if ('MutationObserver' in window) {
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      /* ↗ .cursor/references/code-notes/r-img.js.md#§4 */
      if (mutation.type === 'attributes') {
        const el = mutation.target;
        if (!(el instanceof HTMLImageElement) || !el.matches(SELECTOR)) continue;
        if (mutation.attributeName === LOADED_ATTR && el.hasAttribute(LOADED_ATTR)) continue;

        if (el.complete || el.naturalWidth > 0) {
          markLoaded(el);
        } else {
          el.removeAttribute(LOADED_ATTR);
        }
        if (!el.complete) stallObserver?.observe(el);
        continue;
      }

      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        const el = /** @type {Element} */ (node);
        if (el.matches?.(SELECTOR)) {
          if (/** @type {HTMLImageElement} */ (el).complete) markLoaded(el);
          else stallObserver?.observe(el);
        } else {
          absorb(el);
        }
      }
    }
  });
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['src', 'srcset', LOADED_ATTR],
  });
}
